/**
 * Pitch render subsystem. (§6.3 — "single biggest visual win — invest here")
 *
 * The pitch is ~50% of every frame, so this is the file that decides whether the
 * game reads as EA-Sports-FC broadcast (D0) or as a flat green plane. Everything
 * here is DOWNSTREAM of the sim (§15.2): we only READ `RenderState`, and — being
 * graphics — `Math.*` and the wall clock are fair game. Nothing in this file can
 * change a match outcome, and it never sees a WorldState.
 *
 * What it owns, in order of visual payoff:
 *
 *   1. The GROUND — a 256×256 subdivided plane with ±3cm real-world undulation
 *      (§6.3), lit by a `MeshStandardMaterial` so it picks up the lighting rig's
 *      IBL and receives the sun's shadow FOR FREE (the rig samples any mesh that
 *      sets `receiveShadow`; see lighting.ts). We do NOT hand-roll a raw PBR
 *      shader — that would forfeit IBL/shadow integration and re-couple us to the
 *      lighting subsystem's internals across the one-file-per-agent seam. Instead
 *      we `onBeforeCompile`-inject three broadcast effects into the standard
 *      shader without touching any other subsystem's material:
 *        • MOW STRIPES as ANISOTROPIC ROUGHNESS (§6.3). The alternating light/dark
 *          bands are a *view-dependent specular* effect — grass laid toward the
 *          camera catches the sun and reads light, laid away it reads dark, and
 *          the whole pattern FLIPS when you cut to the far camera. That is what
 *          sells "real broadcast pitch," and it is emphatically NOT a diffuse
 *          checkerboard baked into the albedo. We modulate `roughnessFactor` by
 *          the stripe's lay direction dotted with the world view vector.
 *        • WEAR/DIVOT splat (§6.3) — a generated grayscale mask (worn centre
 *          circle, both goalmouths, both penalty spots) darkens + roughens the
 *          albedo. A real splat map, sized from the match config.
 *        • WETNESS (§6.3, config.wetPitch) — one uniform darkens the albedo and
 *          drops roughness (sharper, wetter specular). "Spectacular for almost
 *          zero cost."
 *      Line markings (§6.3) are drawn at high resolution straight INTO the albedo
 *      texture — not decals, not z-fighting geometry — sized from MatchConfig so a
 *      new pitch size gets correct touchlines/box/circle/arcs.
 *
 *   2. Near-field INSTANCED GPU GRASS (§6.3). Up to 200k blades on Ultra, 0 on Low
 *      (Intel iGPU must hold 60fps, §6.8). Blades live in an `InstancedBufferGeometry`
 *      that TILES AROUND THE CAMERA in the vertex shader (a lattice-snap wrap), so a
 *      fixed pool always covers the <25m near field no matter where play is —
 *      instances are spent where they read. Wind animates in the vertex shader off
 *      `state.elapsed`; a distance falloff fades blades out toward the LOD radius so
 *      they blend into the textured ground rather than pop. Zero per-frame CPU work:
 *      update() sets two uniforms and (on a tier change) the instance count.
 *
 * ── Zero allocation (§15.7) ──────────────────────────────────────────────────
 * update() is the 60Hz hot path and allocates nothing: it mutates preallocated
 * uniform value-objects in place. Every buffer/texture is built in init/
 * onPitchConfig. A tier change only resizes the grass DRAW COUNT — buffers are
 * pre-sized to the Ultra maximum, so no GPU realloc on a quality switch.
 *
 * ── D9 honesty (seam limitation) ─────────────────────────────────────────────
 * The undulation, wear and grass distribution are procedural value-noise, seeded
 * from a fixed constant — NOT downloaded scan data (§16) and NOT the sim's terrain
 * (the sim's pitch is flat; this ±3cm wobble is cosmetic only and never feeds
 * back). The grass is anchored at y≈0 rather than sampling the displaced ground
 * height: at ±3cm and broadcast distance the difference is sub-pixel, and reading
 * the height field per-blade in the shader would cost a texture fetch for no
 * visible gain. Both are documented here rather than faked as something richer.
 */

import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  ClampToEdgeWrapping,
  Color,
  DoubleSide,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  LinearFilter,
  LinearMipmapLinearFilter,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  RepeatWrapping,
  ShaderMaterial,
  SRGBColorSpace,
  Vector2,
  type IUniform,
  type Texture,
  type WebGLRenderer,
} from 'three';
import {
  CENTRE_CIRCLE_RADIUS,
  GOAL_AREA_LENGTH,
  GOAL_AREA_WIDTH,
  GOAL_WIDTH,
  LINE_WIDTH,
  PENALTY_AREA_LENGTH,
  PENALTY_AREA_WIDTH,
  PENALTY_SPOT_DISTANCE,
  type MatchConfig,
} from '@fg/shared/sim';
import {
  type QualitySettings,
  type RenderContext,
  type RenderState,
  type RenderSubsystem,
  IMPOSTOR_LAYER,
} from './context.js';

// ---------------------------------------------------------------------------
// Tunables — pitch-local, cosmetic, never touched by the sim. (§6.3)
// ---------------------------------------------------------------------------

/** Plain-grass surround drawn beyond the touchlines so the field isn't cut at the
 *  very edge of the mesh (metres, each side). */
const SURROUND_M = 10;
/** Subdivided-plane resolution (§6.3 asks for 256×256). */
const GROUND_SEGMENTS = 256;
/** Peak vertical undulation, metres (§6.3 "±3cm"). */
const UNDULATION_M = 0.03;
/** Mow-stripe band width across the LENGTH axis (metres). ~5m reads as broadcast. */
const STRIPE_WIDTH_M = 5.0;
/** How hard the anisotropic bands push roughness (0..1). Subtle — it's specular. */
const STRIPE_CONTRAST = 0.5;
/** Base pitch albedo + roughness (grass is rough; wet drops it). */
const GRASS_ALBEDO = 0x3f7d34;
const GRASS_ROUGHNESS = 0.78;

/** Near-field grass. §6.3: "<25m from camera, ~200k instances." */
const GRASS_RADIUS_M = 25;
/** The wrap lattice tile: blades snap to the nearest tile around the camera, so a
 *  fixed pool tiles the near field infinitely. Must exceed 2×radius to fully cover. */
const GRASS_TILE_M = 2 * GRASS_RADIUS_M + 4;
/** Hard ceiling on the instanced pool (Ultra). Lower tiers draw a sub-range. */
const GRASS_MAX_INSTANCES = 200_000;
const GRASS_BLADE_HEIGHT_MIN = 0.05; // m
const GRASS_BLADE_HEIGHT_MAX = 0.11; // m
const GRASS_BLADE_WIDTH = 0.022; // m at the base
/**
 * FULL-COVERAGE CLUMP LAYER (2026-08-02). The blade field above only exists
 * inside GRASS_RADIUS_M of the camera, so from the broadcast camera most of the
 * pitch showed the flat albedo with a visible circle of detail following the
 * camera -- the "grass is only a patch" defect. This second layer is the cheap
 * complement: static crossed-quad tufts covering the WHOLE pitch + surround in
 * ONE draw call (~4 tris each). They fade OUT close to the camera where the
 * dense blade field takes over, and the blade field's own falloff fades IN over
 * the same band, so the two read as one continuous surface with no popping.
 */
const CLUMP_MAX_INSTANCES = 46_000;
const CLUMP_WIDTH_MIN = 0.14; // m
const CLUMP_WIDTH_MAX = 0.24;
const CLUMP_HEIGHT_MIN = 0.04;
const CLUMP_HEIGHT_MAX = 0.09;
/**
 * Clumps ONLY bridge the blade field's outer edge — they die just past it.
 *
 * Twice now the owner circled scattered dark specks in the MID field, and both
 * times the cause was the same: a sparse quad far enough away to be seen as an
 * individual object no longer reads as grass, it reads as litter. Shrinking
 * and lightening them only made it "a little less". So the range is cut to
 * barely past GRASS_RADIUS_M and the whole mid/far field is carried by the
 * ground albedo's turf speckle instead (see makeAlbedoTexture) — which is also
 * what this project's own brief asks for: texture-based grass by default, no
 * dense geometry across the field.
 */
const CLUMP_FAR_FADE_M = 31;
/**
 * Blades are UNLIT (raw ShaderMaterial writing linear values into the composer's
 * HalfFloat linear buffer; the pmndrs composer applies ACES + sRGB to the whole
 * frame afterwards — grass only exists on medium+, which is always the composer
 * path). The ground, however, is a LIT MeshStandardMaterial: its albedo gets
 * multiplied by (sun·N·L + hemisphere + IBL)/π ≈ 1.7 under lighting.ts's 'day'
 * preset. Live captures showed exactly that mismatch — raw-albedo blades read as
 * yellow-green speckles ~1.7× darker (and hue-shifted) against the lit ground.
 * Fix: derive both blade tints FROM the ground albedo (hue can never drift
 * again) and bake the lit-ground gain in, root slightly under / tip slightly
 * over the ground level so blades model self-shadowed depth, not speckle.
 */
const GRASS_LIT_GAIN = 1.7;

/** Corner-arc radius — Laws of the Game fix this at 1m regardless of pitch size. */
const CORNER_ARC_R = 1.0;
/** The penalty "D" arc radius (centred on the penalty spot). */
const PENALTY_ARC_R = 9.15;

/** Deterministic-ish value noise seed (graphics-only; NOT the sim seed). */
const NOISE_SEED = 1337;

// ---------------------------------------------------------------------------
// Pitch subsystem
// ---------------------------------------------------------------------------

class PitchSystem implements RenderSubsystem {
  readonly name = 'pitch';

  private ctx!: RenderContext;

  /**
   * False when the STADIUM supplies the playing surface instead (the V5 bowl
   * ships its own pitch mesh with the markings modelled, not baked into an
   * albedo). Both draw at y=0, so leaving this on gives z-fighting across the
   * whole field, and the procedural blades/tufts read as wrong on a cel-shaded
   * pitch. Everything else here — the config plumbing, wetness, tier scaling —
   * still runs, so a later toggle back needs no other change.
   */
  private readonly drawSurface: boolean;

  constructor(opts: { drawSurface?: boolean } = {}) {
    this.drawSurface = opts.drawSurface ?? true;
  }

  // ── Ground ─────────────────────────────────────────────────────────────
  private groundMesh: Mesh | null = null;
  private groundGeom: BufferGeometry | null = null;
  private groundMat!: MeshStandardMaterial;
  private albedoTex: Texture | null = null;
  private wearTex: Texture | null = null;
  /** Injected uniforms, owned here so update() can mutate them zero-alloc even
   *  before three compiles the material (Object.assign'd into shader.uniforms). */
  private readonly groundUni: {
    uElapsed: IUniform<number>;
    uWet: IUniform<number>;
    uStripeWidth: IUniform<number>;
    uStripeContrast: IUniform<number>;
    uHalfExtL: IUniform<number>;
    uHalfExtW: IUniform<number>;
  } = {
    uElapsed: { value: 0 },
    uWet: { value: 0 },
    uStripeWidth: { value: STRIPE_WIDTH_M },
    uStripeContrast: { value: STRIPE_CONTRAST },
    uHalfExtL: { value: 60 },
    uHalfExtW: { value: 40 },
  };

  // ── Grass ──────────────────────────────────────────────────────────────
  private grassMesh: Mesh | null = null;
  private grassGeom: InstancedBufferGeometry | null = null;
  private grassMat!: ShaderMaterial;
  private grassCount = 0;
  private clumpMesh: Mesh | null = null;
  private clumpGeom: InstancedBufferGeometry | null = null;
  private clumpMat: ShaderMaterial | null = null;
  private lastQuality: QualitySettings | null = null;

  private wet = false;

  init(ctx: RenderContext): void {
    this.ctx = ctx;

    // Base material: standard PBR so the lighting rig's IBL + sun shadow apply for
    // free (§6.2). We inject the broadcast effects via onBeforeCompile below.
    this.groundMat = new MeshStandardMaterial({
      color: 0xffffff, // real colour comes from the albedo map
      roughness: GRASS_ROUGHNESS,
      metalness: 0.0,
    });
    this.installGroundShader(this.groundMat);
    this.buildGrass(ctx);
    this.applyTier(ctx.quality);
  }

  onPitchConfig(config: MatchConfig, ctx: RenderContext): void {
    this.wet = config.wetPitch;
    this.groundUni.uWet.value = this.wet ? 1 : 0;
    if (this.grassMat) this.grassMat.uniforms.uWet!.value = this.wet ? 1 : 0;
    if (this.clumpMat) this.clumpMat.uniforms.uWet!.value = this.wet ? 1 : 0;
    this.rebuildGround(config, ctx);
  }

  update(state: RenderState, ctx: RenderContext): void {
    // Zero-alloc: only mutate preallocated uniform value-objects.
    this.groundUni.uElapsed.value = state.elapsed;

    if (this.grassMesh && this.grassMesh.visible) {
      const u = this.grassMat.uniforms;
      u.uElapsed!.value = state.elapsed;
      // Snap the wrap lattice to the camera's ground position (mutate the Vector2
      // in place — no new object).
      (u.uCamXZ!.value as Vector2).set(ctx.camera.position.x, ctx.camera.position.z);
    }
    if (this.clumpMat && this.clumpMesh && this.clumpMesh.visible) {
      const u = this.clumpMat.uniforms;
      u.uElapsed!.value = state.elapsed;
      (u.uCamXZ!.value as Vector2).set(ctx.camera.position.x, ctx.camera.position.z);
    }
  }

  setQuality(quality: QualitySettings, ctx: RenderContext): void {
    this.applyTier(quality);
    void ctx;
  }

  onResize(ctx: RenderContext): void {
    // Point size / density scales with pixel ratio so blades stay ~constant on
    // screen across DPI. Cheap uniform poke.
    if (this.grassMat) {
      this.grassMat.uniforms.uPixelRatio!.value = Math.min(ctx.gl.getPixelRatio(), 2);
    }
  }

  dispose(): void {
    if (this.groundMesh) this.ctx.scene.remove(this.groundMesh);
    this.groundGeom?.dispose();
    this.groundMat.dispose();
    this.albedoTex?.dispose();
    this.wearTex?.dispose();
    if (this.grassMesh) this.ctx.scene.remove(this.grassMesh);
    this.grassGeom?.dispose();
    this.grassMat?.dispose();
    if (this.clumpMesh) this.ctx.scene.remove(this.clumpMesh);
    this.clumpGeom?.dispose();
    this.clumpMat?.dispose();
  }

  // ── Tier scaling (§6.8) ────────────────────────────────────────────────
  private applyTier(quality: QualitySettings): void {
    this.lastQuality = quality;
    // grassInstances is the tier's near-field budget (0 on Low → no GPU grass at
    // all on an Intel iGPU). Clamp to the pre-sized pool and draw a sub-range;
    // never realloc a GPU buffer on a tier switch.
    this.grassCount = Math.min(quality.grassInstances, GRASS_MAX_INSTANCES);
    if (this.grassGeom) this.grassGeom.instanceCount = this.grassCount;
    if (this.grassMesh) this.grassMesh.visible = this.drawSurface && this.grassCount > 0;
    // LOD radius follows lodBias so a lower tier drops the near field sooner.
    if (this.grassMat) {
      this.grassMat.uniforms.uRadius!.value = GRASS_RADIUS_M * Math.max(0.5, quality.lodBias);
    }
    // Clumps ride the same tier switch: no GPU grass on Low means no clumps
    // either, and lower tiers draw a sub-range of the pool (they are ~1% of the
    // blade field's triangle cost, so the fraction is generous).
    if (this.clumpGeom) {
      const frac = quality.grassInstances <= 0 ? 0
        : quality.grassInstances >= GRASS_MAX_INSTANCES ? 1 : 0.6;
      this.clumpGeom.instanceCount = Math.floor(CLUMP_MAX_INSTANCES * frac);
      if (this.clumpMesh) this.clumpMesh.visible = this.drawSurface && this.clumpGeom.instanceCount > 0;
    }
  }

  // ── Ground geometry + textures (rebuilt per match config, §9.2) ─────────
  private rebuildGround(config: MatchConfig, ctx: RenderContext): void {
    // Tear down the previous match's ground.
    if (this.groundMesh) {
      ctx.scene.remove(this.groundMesh);
      this.groundMesh = null;
    }
    this.groundGeom?.dispose();
    this.albedoTex?.dispose();
    this.wearTex?.dispose();

    const extLength = config.pitchLength + SURROUND_M * 2;
    const extWidth = config.pitchWidth + SURROUND_M * 2;
    this.groundUni.uHalfExtL.value = extLength * 0.5;
    this.groundUni.uHalfExtW.value = extWidth * 0.5;

    // Subdivided plane, rotated flat (default plane faces +Z). The sim's length
    // axis is X and width axis is Z (goals at ±pitchLength/2), so map plane-U→X.
    const geom = new PlaneGeometry(extLength, extWidth, GROUND_SEGMENTS, GROUND_SEGMENTS);
    geom.rotateX(-Math.PI / 2); // now spans X (length) × Z (width), normal +Y
    displaceUndulation(geom);
    geom.computeVertexNormals();
    this.groundGeom = geom;

    // Line markings baked into the albedo at high res (§6.3 — into the albedo, not
    // decals/geometry). Wear as its own splat map.
    this.albedoTex = makeAlbedoTexture(config, extLength, extWidth);
    this.wearTex = makeWearTexture(config, extLength, extWidth);

    this.groundMat.map = this.albedoTex;
    this.groundMat.needsUpdate = true; // map added after first compile → recompile
    // Re-point the wear sampler on the (possibly already compiled) shader.
    this.groundUni_wearMap.value = this.wearTex;

    const mesh = new Mesh(geom, this.groundMat);
    mesh.name = 'pitch-ground';
    mesh.receiveShadow = true; // the lighting rig's sun shadow lands here (§6.2)
    mesh.frustumCulled = false;
    mesh.visible = this.drawSurface;
    // Draw the pitch first so grass/players/nets sort on top of it.
    mesh.renderOrder = -1;
    ctx.scene.add(mesh);
    this.groundMesh = mesh;

    this.rebuildClumps(config, ctx);
  }

  /** Static full-pitch tuft layer -- see CLUMP_MAX_INSTANCES for the why. */
  private rebuildClumps(config: MatchConfig, ctx: RenderContext): void {
    if (this.clumpMesh) { ctx.scene.remove(this.clumpMesh); this.clumpMesh = null; }
    this.clumpGeom?.dispose();
    this.clumpMat?.dispose();

    const extLength = config.pitchLength + SURROUND_M * 2;
    const extWidth = config.pitchWidth + SURROUND_M * 2;

    // Crossed pair of quads (an X from above) so a static clump reads from every
    // camera yaw without billboarding. 4 tris per instance.
    const pos = new Float32Array([
      -0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0,
      0, 0, -0.5, 0, 0, 0.5, 0, 1, 0.5, 0, 0, -0.5, 0, 1, 0.5, 0, 1, -0.5,
    ]);
    const base = new BufferGeometry();
    base.setAttribute('position', new BufferAttribute(pos, 3));

    const geom = new InstancedBufferGeometry();
    geom.setAttribute('position', base.getAttribute('position'));
    const offset = new Float32Array(CLUMP_MAX_INSTANCES * 2);
    const params = new Float32Array(CLUMP_MAX_INSTANCES * 4); // width, height, phase, tint
    const rng = mulberry32(NOISE_SEED ^ 0x51f7);
    for (let i = 0; i < CLUMP_MAX_INSTANCES; i++) {
      offset[i * 2] = (rng() - 0.5) * extLength;
      offset[i * 2 + 1] = (rng() - 0.5) * extWidth;
      params[i * 4] = CLUMP_WIDTH_MIN + rng() * (CLUMP_WIDTH_MAX - CLUMP_WIDTH_MIN);
      params[i * 4 + 1] = CLUMP_HEIGHT_MIN + rng() * (CLUMP_HEIGHT_MAX - CLUMP_HEIGHT_MIN);
      params[i * 4 + 2] = rng() * Math.PI * 2;
      params[i * 4 + 3] = 0.92 + rng() * 0.16;
    }
    geom.setAttribute('aOffset', new InstancedBufferAttribute(offset, 2));
    geom.setAttribute('aParam', new InstancedBufferAttribute(params, 4));
    geom.instanceCount = 0;
    this.clumpGeom = geom;

    this.clumpMat = new ShaderMaterial({
      uniforms: {
        uElapsed: { value: 0 },
        uCamXZ: { value: new Vector2() },
        uNearRadius: { value: GRASS_RADIUS_M },
        uFar: { value: CLUMP_FAR_FADE_M },
        uStripeWidth: { value: STRIPE_WIDTH_M },
        uStripeContrast: { value: STRIPE_CONTRAST },
        uWet: { value: this.wet ? 1 : 0 },
        uBase: { value: new Color(GRASS_ALBEDO).multiplyScalar(GRASS_LIT_GAIN * 0.94) },
        uTip: { value: new Color(GRASS_ALBEDO).multiplyScalar(GRASS_LIT_GAIN * 1.08) },
      },
      vertexShader: /* glsl */ `
        attribute vec2 aOffset;
        attribute vec4 aParam; // width, height, phase, tint
        uniform float uElapsed;
        uniform vec2 uCamXZ;
        uniform float uNearRadius;
        uniform float uFar;
        varying float vH;
        varying float vFade;
        varying float vTint;
        varying vec3 vWorldG;
        void main() {
          vTint = aParam.w;
          float hP = position.y;
          vH = hP;
          vec3 world = vec3(aOffset.x, 0.0, aOffset.y);
          world.x += position.x * aParam.x;
          world.z += position.z * aParam.x;
          world.y += hP * aParam.y;
          // Gentle shared-gust sway, root planted.
          float gust = sin(uElapsed * 1.6 + aParam.z + aOffset.x * 0.18 + aOffset.y * 0.15);
          world.x += gust * 0.035 * hP * hP;
          float dist = distance(aOffset, uCamXZ);
          // Fade OUT under the dense blade field, and out again into the far haze.
          float nearFade = smoothstep(uNearRadius * 0.55, uNearRadius * 0.95, dist);
          float farFade = 1.0 - smoothstep(uFar * 0.55, uFar, dist);
          vFade = nearFade * farFade;
          vWorldG = world;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform vec3 uBase;
        uniform vec3 uTip;
        uniform vec2 uCamXZ;
        uniform float uStripeWidth;
        uniform float uStripeContrast;
        uniform float uWet;
        varying float vH;
        varying float vFade;
        varying float vTint;
        varying vec3 vWorldG;
        void main() {
          if (vFade <= 0.02) discard;
          vec3 col = mix(uBase, uTip, vH) * vTint;
          float idx = floor(vWorldG.x / uStripeWidth);
          float lay = mod(idx, 2.0) < 1.0 ? 1.0 : -1.0;
          vec3 vw = normalize(vec3(uCamXZ.x, 0.0, uCamXZ.y) - vWorldG);
          vec2 viewXZ = normalize(vec2(vw.x, vw.z) + 1e-5);
          float aniso = clamp(lay * viewXZ.y, -1.0, 1.0);
          col *= (1.0 + uStripeContrast * 0.35 * aniso);
          col *= mix(1.0, 0.62, uWet);
          col *= mix(0.9, 1.0, vH);
          gl_FragColor = vec4(col, vFade);
        }`,
      side: DoubleSide,
      transparent: true,
      depthWrite: true,
    });

    const mesh = new Mesh(geom, this.clumpMat);
    mesh.name = 'pitch-grass-clumps';
    mesh.frustumCulled = false;
    // Every clump is placed by the vertex shader from aOffset/aParam; the base
    // geometry is a crossed unit quad at the origin. Any pass that swaps the
    // material loses that placement and stacks all 27,600 of them on the centre
    // spot. See IMPOSTOR_LAYER.
    mesh.layers.set(IMPOSTOR_LAYER);
    mesh.visible = false;
    mesh.renderOrder = 0;
    ctx.scene.add(mesh);
    this.clumpMesh = mesh;
    if (this.lastQuality) this.applyTier(this.lastQuality);
  }

  // Wear sampler kept as a standalone uniform object so rebuildGround can swap the
  // texture without recompiling and without a fresh object each match.
  private readonly groundUni_wearMap: IUniform<Texture | null> = { value: null };

  private installGroundShader(mat: MeshStandardMaterial): void {
    mat.onBeforeCompile = (shader) => {
      // Share our persistent uniform objects into the compiled program so update()
      // keeps driving them by reference.
      shader.uniforms.uElapsed = this.groundUni.uElapsed;
      shader.uniforms.uWet = this.groundUni.uWet;
      shader.uniforms.uStripeWidth = this.groundUni.uStripeWidth;
      shader.uniforms.uStripeContrast = this.groundUni.uStripeContrast;
      shader.uniforms.uHalfExtL = this.groundUni.uHalfExtL;
      shader.uniforms.uHalfExtW = this.groundUni.uHalfExtW;
      shader.uniforms.uWearMap = this.groundUni_wearMap;

      // --- Vertex: forward the world position for stripe/wear/view math. --------
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>\n varying vec3 vWorldPitch;`,
        )
        .replace(
          '#include <project_vertex>',
          `#include <project_vertex>\n vWorldPitch = (modelMatrix * vec4(transformed, 1.0)).xyz;`,
        );

      // --- Fragment: declarations. ---------------------------------------------
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        `#include <common>
         varying vec3 vWorldPitch;
         uniform float uElapsed;
         uniform float uWet;
         uniform float uStripeWidth;
         uniform float uStripeContrast;
         uniform float uHalfExtL;
         uniform float uHalfExtW;
         uniform sampler2D uWearMap;

         // Alternating mow-stripe lay direction along the length (X) axis. Returns
         // +1 / -1: the grass in this band lies toward +Z or -Z.
         float stripeLay(float x) {
           float idx = floor(x / uStripeWidth);
           return mod(idx, 2.0) < 1.0 ? 1.0 : -1.0;
         }`,
      );

      // --- Fragment: wear + wetness fold into the albedo. -----------------------
      // Runs right after the base map is sampled into diffuseColor.
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <map_fragment>',
        `#include <map_fragment>
         {
           // Wear splat (linear grayscale): worn turf is browner + darker.
           vec2 wuv = vec2(
             vWorldPitch.x / (2.0 * uHalfExtL) + 0.5,
             vWorldPitch.z / (2.0 * uHalfExtW) + 0.5
           );
           float wear = texture2D(uWearMap, wuv).r;
           vec3 wornCol = diffuseColor.rgb * vec3(0.78, 0.66, 0.5);
           diffuseColor.rgb = mix(diffuseColor.rgb, wornCol, wear * 0.75);
           // Wet pitch: darker albedo (soaked grass reads deeper green).
           diffuseColor.rgb *= mix(1.0, 0.62, uWet);
         }`,
      );

      // --- Fragment: mow stripes as anisotropic roughness + wetness. ------------
      // Runs right after roughnessFactor is established.
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
         {
           // View vector, projected to the ground plane, in WORLD space.
           vec3 vw = normalize(cameraPosition - vWorldPitch);
           vec2 viewXZ = normalize(vec2(vw.x, vw.z) + 1e-5);
           float lay = stripeLay(vWorldPitch.x);
           // Anisotropy: +1 when you look along the lay (grass laid toward you →
           // catches the sun → LOWER roughness → brighter band); flips at the far
           // camera. This is the whole broadcast-stripe trick (§6.3).
           float aniso = clamp(lay * viewXZ.y, -1.0, 1.0);
           roughnessFactor *= (1.0 - uStripeContrast * aniso);
           // Wet pitch sharpens the specular everywhere (roughness ↓).
           // Wet floor 0.78→~0.48, not ×0.4 (→0.31): under the six night
           // floodlights a 0.31-rough pitch reads as a glare mirror — measured
           // 14.7% clipped pixels vs 3.2% after this + flood/bloom trims
           // (user report "game is shining", 2026-07-18).
           roughnessFactor = mix(roughnessFactor, roughnessFactor * 0.62, uWet);
           roughnessFactor = clamp(roughnessFactor, 0.04, 1.0);
         }`,
      );
    };
  }

  // ── Near-field instanced grass (§6.3) ──────────────────────────────────
  private buildGrass(ctx: RenderContext): void {
    // Blade = a single tapered, camera-billboarded quad (2 tris). At 200k that is
    // 400k tris — comfortably inside the §6.8 budget, and the billboard keeps every
    // blade face-on so none vanish edge-on.
    const base = new BufferGeometry();
    // local: x = side param (-0.5..0.5), y = height param (0..1)
    const pos = new Float32Array([
      -0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0,
      -0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0,
    ]);
    base.setAttribute('position', new BufferAttribute(pos, 3));

    const geom = new InstancedBufferGeometry();
    geom.index = base.index;
    geom.setAttribute('position', base.getAttribute('position'));

    // Per-instance base position within one lattice tile, plus rotation/height/tint.
    const offset = new Float32Array(GRASS_MAX_INSTANCES * 2);
    const params = new Float32Array(GRASS_MAX_INSTANCES * 3); // height, phase, tint
    const rng = mulberry32(NOISE_SEED ^ 0x9e37);
    const half = GRASS_TILE_M * 0.5;
    for (let i = 0; i < GRASS_MAX_INSTANCES; i++) {
      offset[i * 2] = (rng() - 0.5) * GRASS_TILE_M;
      offset[i * 2 + 1] = (rng() - 0.5) * GRASS_TILE_M;
      params[i * 3] =
        GRASS_BLADE_HEIGHT_MIN + rng() * (GRASS_BLADE_HEIGHT_MAX - GRASS_BLADE_HEIGHT_MIN);
      params[i * 3 + 1] = rng() * Math.PI * 2; // wind phase
      params[i * 3 + 2] = 0.85 + rng() * 0.3; // per-blade tint multiplier
    }
    void half;
    geom.setAttribute('aOffset', new InstancedBufferAttribute(offset, 2));
    geom.setAttribute('aParam', new InstancedBufferAttribute(params, 3));
    geom.instanceCount = 0;
    this.grassGeom = geom;

    this.grassMat = new ShaderMaterial({
      uniforms: {
        uElapsed: { value: 0 },
        uCamXZ: { value: new Vector2() },
        uRadius: { value: GRASS_RADIUS_M },
        uTile: { value: GRASS_TILE_M },
        uWidth: { value: GRASS_BLADE_WIDTH },
        uStripeWidth: { value: STRIPE_WIDTH_M },
        uStripeContrast: { value: STRIPE_CONTRAST },
        uWet: { value: 0 },
        uPixelRatio: { value: Math.min(ctx.gl.getPixelRatio(), 2) },
        // new Color(hex) converts sRGB→linear (three r152+ ColorManagement), so
        // these land in the same space as the ground's sampled sRGB albedo map.
        // GRASS_LIT_GAIN then matches the ground's lighting response — see the
        // constant's comment for the full colorspace story.
        uBase: { value: new Color(GRASS_ALBEDO).multiplyScalar(GRASS_LIT_GAIN * 0.82) },
        uTip: { value: new Color(GRASS_ALBEDO).multiplyScalar(GRASS_LIT_GAIN * 1.18) },
      },
      vertexShader: /* glsl */ `
        attribute vec2 aOffset;
        attribute vec3 aParam; // height, phase, tint
        uniform float uElapsed;
        uniform vec2 uCamXZ;
        uniform float uRadius;
        uniform float uTile;
        uniform float uWidth;
        varying float vH;      // height param up the blade
        varying float vFade;   // distance fade 1..0
        varying float vTint;
        varying vec3 vWorldG;
        void main() {
          float height = aParam.x;
          float phase = aParam.y;
          vTint = aParam.z;

          // Lattice-snap the blade to the tile nearest the camera → a fixed pool
          // tiles the near field with correct parallax (blades stay ground-anchored).
          vec2 anchor = aOffset + uTile * floor((uCamXZ - aOffset) / uTile + 0.5);
          float dist = distance(anchor, uCamXZ);
          // LOD/density falloff: fade + shrink toward the radius so blades melt into
          // the textured ground instead of popping (§6.3 near/far blend).
          vFade = 1.0 - smoothstep(uRadius * 0.7, uRadius, dist);
          height *= vFade;

          // Camera-billboard the blade around its vertical axis.
          vec3 camDir = normalize(vec3(uCamXZ.x, 0.0, uCamXZ.y) - vec3(anchor.x, 0.0, anchor.y) + 1e-4);
          vec3 side = normalize(cross(vec3(0.0, 1.0, 0.0), camDir));

          float sideP = position.x;      // -0.5..0.5
          float hP = position.y;         // 0..1
          vH = hP;
          float taper = 1.0 - 0.6 * hP;  // narrower toward the tip

          vec3 world = vec3(anchor.x, 0.0, anchor.y);
          world += side * (sideP * uWidth * taper);
          world.y += hP * height;
          // Vertex-shader wind: bend the tip along a travelling gust; quadratic in
          // height so the root stays planted. (§6.3)
          float gust = sin(uElapsed * 1.8 + phase + anchor.x * 0.25 + anchor.y * 0.2);
          float gust2 = sin(uElapsed * 3.3 + phase * 1.7);
          float bend = (gust * 0.7 + gust2 * 0.3) * 0.06 * hP * hP;
          world.x += bend;
          world.z += bend * 0.6;

          vWorldG = world;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform vec3 uBase;
        uniform vec3 uTip;
        uniform vec2 uCamXZ;
        uniform float uStripeWidth;
        uniform float uStripeContrast;
        uniform float uWet;
        varying float vH;
        varying float vFade;
        varying float vTint;
        varying vec3 vWorldG;
        void main() {
          if (vFade <= 0.01) discard;
          // Root→tip gradient, per-blade tint, matched to the ground stripe so the
          // near grass and the far texture read as one surface.
          vec3 col = mix(uBase, uTip, vH) * vTint;
          float idx = floor(vWorldG.x / uStripeWidth);
          float lay = mod(idx, 2.0) < 1.0 ? 1.0 : -1.0;
          vec3 vw = normalize(vec3(uCamXZ.x, 0.0, uCamXZ.y) - vWorldG);
          vec2 viewXZ = normalize(vec2(vw.x, vw.z) + 1e-5);
          float aniso = clamp(lay * viewXZ.y, -1.0, 1.0);
          col *= (1.0 + uStripeContrast * 0.35 * aniso);
          col *= mix(1.0, 0.62, uWet);
          // Fake AO toward the root + soft edge on distance fade. 0.72 (was
          // 0.55): the deeper floor left dark speckle against the lit ground.
          col *= mix(0.72, 1.0, vH);
          gl_FragColor = vec4(col, vFade);
        }`,
      side: DoubleSide,
      transparent: true,
      depthWrite: true,
    });

    const mesh = new Mesh(geom, this.grassMat);
    mesh.name = 'pitch-grass';
    mesh.frustumCulled = false;
    // As above, and this is the big one: up to 120,000 blades on High, each
    // positioned entirely in the vertex shader. See IMPOSTOR_LAYER.
    mesh.layers.set(IMPOSTOR_LAYER);
    mesh.visible = false;
    mesh.renderOrder = 0;
    ctx.scene.add(mesh);
    this.grassMesh = mesh;
    void RepeatWrapping;
  }
}

/**
 * @param opts.drawSurface pass FALSE when the stadium supplies its own playing
 *   surface (the V5 bowl does) — see `PitchSystem.drawSurface`.
 */
export function createPitchSystem(opts: { drawSurface?: boolean } = {}): RenderSubsystem {
  return new PitchSystem(opts);
}

// ─────────────────────────────────────────────────────────────────────────────
// Procedural helpers — synthesized in-code, nothing downloaded (§16).
// ─────────────────────────────────────────────────────────────────────────────

/** Small deterministic PRNG for cosmetic scatter (NOT the sim RNG). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Value noise sampled on a hashed lattice — cheap, seamless enough for ±3cm. */
function valueNoise(x: number, y: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const h = (ix: number, iy: number): number => {
    let n = (ix * 374761393 + iy * 668265263 + NOISE_SEED) | 0;
    n = (n ^ (n >>> 13)) * 1274126177;
    return (((n ^ (n >>> 16)) >>> 0) / 4294967296) * 2 - 1;
  };
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = h(xi, yi);
  const b = h(xi + 1, yi);
  const c = h(xi, yi + 1);
  const d = h(xi + 1, yi + 1);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}

/** Displace a flat plane's Y by layered value noise (±UNDULATION_M). Zero-alloc
 *  aside from reading the existing position buffer in place. (§6.3) */
function displaceUndulation(geom: BufferGeometry): void {
  const pos = geom.getAttribute('position') as BufferAttribute;
  const arr = pos.array as Float32Array;
  for (let i = 0; i < arr.length; i += 3) {
    const x = arr[i]!;
    const z = arr[i + 2]!;
    const n =
      valueNoise(x * 0.12, z * 0.12) * 0.65 + valueNoise(x * 0.4, z * 0.4) * 0.35;
    arr[i + 1] = n * UNDULATION_M;
  }
  pos.needsUpdate = true;
}

/**
 * The high-res albedo: base green + all line markings (§6.3), sized from the match
 * config. Markings that have fixed real-world dimensions (penalty area, circle,
 * arcs) are scaled toward the pitch size so small-sided pitches keep sane,
 * inside-the-touchline boxes rather than overflowing.
 */
function makeAlbedoTexture(config: MatchConfig, extLength: number, extWidth: number): Texture {
  const W = 2048;
  const H = Math.max(256, Math.round((W * extWidth) / extLength));
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d')!;

  // Canvas 2D is sRGB and the texture is flagged SRGBColorSpace below, so paint
  // the palette's sRGB bytes directly. (Bug fixed here: `new Color(hex).r` is
  // LINEAR in three r152+ — writing those components to the canvas double-
  // darkened the whole pitch, 63/125/52 → 13/53/9, found by sampling the live
  // texture during render QA.)
  const base = new Color(GRASS_ALBEDO);
  g.fillStyle = `#${base.getHexString()}`;
  g.fillRect(0, 0, W, H);

  // FINE TURF SPECKLE (2026-08-02). Full-pitch grass coverage is a TEXTURE
  // job, not a geometry job: the scattered-quad clump layer that used to do it
  // read as dark confetti in the mid field (owner screenshots) because sparse
  // quads stop resembling grass the moment they are far enough apart to be
  // seen individually. Thousands of sub-pixel-at-distance flecks in the albedo
  // give the same "covered in grass" impression with ZERO geometry, zero draw
  // calls and zero per-frame cost, and they mip down to clean average colour
  // instead of aliasing. Painted before the markings so lines stay crisp.
  const speckRng = mulberry32(NOISE_SEED ^ 0x2b17);
  for (let i = 0; i < 90_000; i++) {
    const x = speckRng() * W;
    const y = speckRng() * H;
    const t = speckRng();
    // Half lighter, half darker, all within a few percent of the base — this
    // is texture, not contrast.
    const shade = t < 0.5 ? 1.0 + t * 0.14 : 1.0 - (t - 0.5) * 0.16;
    const r = Math.round(Math.min(255, base.r * 255 * shade * 1.9));
    const gg = Math.round(Math.min(255, base.g * 255 * shade * 1.32));
    const bb = Math.round(Math.min(255, base.b * 255 * shade * 1.9));
    g.fillStyle = `rgba(${r},${gg},${bb},0.5)`;
    g.fillRect(x, y, 1 + (t > 0.8 ? 1 : 0), 1 + (t > 0.9 ? 1 : 0));
  }

  // World (metres, origin at pitch centre) → canvas px.
  const sx = W / extLength;
  const sy = H / extWidth;
  const toPx = (x: number): number => (x + extLength / 2) * sx;
  const toPy = (z: number): number => (z + extWidth / 2) * sy;
  const linepx = Math.max(2, LINE_WIDTH * ((sx + sy) / 2));

  g.strokeStyle = 'rgba(245,248,245,0.92)';
  g.fillStyle = 'rgba(245,248,245,0.92)';
  g.lineWidth = linepx;
  g.lineCap = 'butt';

  const halfL = config.pitchLength / 2;
  const halfW = config.pitchWidth / 2;
  // Scale fixed-size features so they fit the (possibly small-sided) pitch.
  const scaleL = Math.min(1, config.pitchLength / 105);
  const scaleW = Math.min(1, config.pitchWidth / 68);
  const scaleMin = Math.min(scaleL, scaleW);

  const rect = (x0: number, z0: number, x1: number, z1: number): void => {
    g.strokeRect(toPx(x0), toPy(z0), toPx(x1) - toPx(x0), toPy(z1) - toPy(z0));
  };
  const line = (x0: number, z0: number, x1: number, z1: number): void => {
    g.beginPath();
    g.moveTo(toPx(x0), toPy(z0));
    g.lineTo(toPx(x1), toPy(z1));
    g.stroke();
  };
  const dot = (x: number, z: number): void => {
    g.beginPath();
    g.arc(toPx(x), toPy(z), linepx * 1.3, 0, Math.PI * 2);
    g.fill();
  };
  // Ellipse in world space (radius may map to different px per axis).
  const arc = (
    cx: number,
    cz: number,
    r: number,
    a0: number,
    a1: number,
  ): void => {
    g.beginPath();
    g.ellipse(toPx(cx), toPy(cz), r * sx, r * sy, 0, a0, a1);
    g.stroke();
  };

  // Touchlines + goal lines (the outer boundary).
  rect(-halfL, -halfW, halfL, halfW);
  // Halfway line + centre circle + centre spot.
  line(0, -halfW, 0, halfW);
  arc(0, 0, CENTRE_CIRCLE_RADIUS * scaleMin, 0, Math.PI * 2);
  dot(0, 0);

  const paLen = PENALTY_AREA_LENGTH * scaleL;
  const paHalfW = (PENALTY_AREA_WIDTH * scaleW) / 2;
  const gaLen = GOAL_AREA_LENGTH * scaleL;
  const gaHalfW = (GOAL_AREA_WIDTH * scaleW) / 2;
  const spot = PENALTY_SPOT_DISTANCE * scaleL;
  const arcR = PENALTY_ARC_R * scaleMin;

  for (const sign of [-1, 1]) {
    const goalX = sign * halfL;
    // Penalty area.
    rect(goalX, -paHalfW, goalX - sign * paLen, paHalfW);
    // Six-yard (goal) area.
    rect(goalX, -gaHalfW, goalX - sign * gaLen, gaHalfW);
    // Penalty spot.
    const spotX = goalX - sign * spot;
    dot(spotX, 0);
    // Penalty "D" arc — only the segment OUTSIDE the box.
    const half = Math.acos(Math.min(1, Math.max(-1, (spot - paLen) / arcR)));
    if (sign > 0) arc(spotX, 0, arcR, Math.PI - half, Math.PI + half);
    else arc(spotX, 0, arcR, -half, half);
    // Corner arcs (quarter circles at the two goal-line corners).
    for (const sz of [-1, 1]) {
      const cz = sz * halfW;
      const a0 = sign > 0 ? Math.PI : 0;
      // Draw the quarter facing into the pitch.
      const start = sz > 0 ? a0 - Math.PI / 2 : a0;
      arc(goalX, cz, CORNER_ARC_R * scaleMin, start, start + Math.PI / 2);
    }
  }

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = 8;
  tex.wrapS = ClampToEdgeWrapping;
  tex.wrapT = ClampToEdgeWrapping;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.magFilter = LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/**
 * The wear/divot splat map (§6.3): grayscale, R = wear amount. Painted soft blobs
 * at the worn centre circle, both goalmouths and both penalty spots — the places a
 * real pitch is chewed up by kickoff, keepers and spot-kicks.
 */
function makeWearTexture(config: MatchConfig, extLength: number, extWidth: number): Texture {
  const W = 1024;
  const H = Math.max(128, Math.round((W * extWidth) / extLength));
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d')!;
  g.fillStyle = '#000';
  g.fillRect(0, 0, W, H);

  const sx = W / extLength;
  const sy = H / extWidth;
  const toPx = (x: number): number => (x + extLength / 2) * sx;
  const toPy = (z: number): number => (z + extWidth / 2) * sy;
  const avgS = (sx + sy) / 2;

  const blob = (x: number, z: number, rM: number, strength: number): void => {
    const cx = toPx(x);
    const cz = toPy(z);
    const r = rM * avgS;
    const grd = g.createRadialGradient(cx, cz, 0, cx, cz, r);
    grd.addColorStop(0, `rgba(255,255,255,${strength})`);
    grd.addColorStop(0.6, `rgba(255,255,255,${strength * 0.45})`);
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.globalCompositeOperation = 'lighter';
    g.fillStyle = grd;
    g.beginPath();
    g.arc(cx, cz, r, 0, Math.PI * 2);
    g.fill();
  };

  const halfL = config.pitchLength / 2;
  const scaleL = Math.min(1, config.pitchLength / 105);
  // Centre-circle churn from every kickoff.
  blob(0, 0, 3.0, 0.55);
  for (const sign of [-1, 1]) {
    const goalX = sign * halfL;
    // Goalmouth: a wide worn band across the goal, a couple of metres deep.
    blob(goalX - sign * 1.6, 0, GOAL_WIDTH * 0.6, 0.7);
    blob(goalX - sign * 3.0, 0, GOAL_WIDTH * 0.4, 0.4);
    // Penalty spot.
    blob(goalX - sign * PENALTY_SPOT_DISTANCE * scaleL, 0, 1.1, 0.85);
  }
  g.globalCompositeOperation = 'source-over';

  const tex = new CanvasTexture(canvas);
  // Wear is a linear mask, not colour — leave in the default (no-sRGB) space.
  tex.wrapS = ClampToEdgeWrapping;
  tex.wrapT = ClampToEdgeWrapping;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

// Keep these imports meaningful to readers even where only used via three's
// internal paths / future toggles.
export type { WebGLRenderer };
